<template>
  <main class="app-main contracts-page">
    <div class="app-content-header">
      <div class="container-fluid">
        <ol class="breadcrumb mb-3">
          <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
          <li class="breadcrumb-item"><router-link to="/contracts">منصة العقود العقارية</router-link></li>
          <li class="breadcrumb-item active">تفاصيل العقد</li>
        </ol>
        <div class="contract-heading">
          <div>
            <h1>{{ row?.title || 'تفاصيل العقد العقاري' }}</h1>
            <p v-if="row">{{ row.code }} · {{ row.typeName }}</p>
          </div>
          <div v-if="row" class="contract-actions contract-no-print">
            <button type="button" class="btn btn-outline-primary" @click="printSummary">
              <i class="bi bi-printer ms-1" aria-hidden="true"></i>
              طباعة ملخص العقد
            </button>
            <router-link v-if="canUpdate" :to="`/contracts/update/${row.id}`" class="btn btn-primary">
              <i class="bi bi-pencil-square ms-1" aria-hidden="true"></i>
              تعديل العقد
            </router-link>
            <router-link to="/contracts" class="btn btn-outline-secondary">سجل العقود</router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="app-content">
      <div class="container-fluid">
        <div v-if="!canRead" class="contract-card contract-access-denied">ليس لديك صلاحية عرض تفاصيل العقود.</div>
        <template v-else>
          <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
          <div v-if="loading" class="py-4"><mainLoader /></div>
          <div v-else-if="!row" class="contract-card contract-empty">
            <i class="bi bi-file-earmark-x" aria-hidden="true"></i>
            <h3>تعذر عرض العقد</h3>
            <button type="button" class="btn btn-outline-primary" @click="load">إعادة المحاولة</button>
          </div>
          <template v-else>
            <div class="contract-form-grid">
              <div class="contract-form-main">
                <section class="contract-card" aria-labelledby="contract-summary-title">
                  <div class="contract-card-header">
                    <div class="d-flex align-items-center gap-2">
                      <img src="/assets/img/brand-mark.svg" alt="العقارية" width="36" height="36" />
                      <h2 id="contract-summary-title">ملخص العقد العقاري</h2>
                    </div>
                    <span class="contract-status" :class="`contract-status-${row.effectiveStatus}`">
                      {{ statusLabel(row.effectiveStatus) }}
                    </span>
                  </div>
                  <div class="contract-card-body">
                    <dl class="contract-definition">
                      <div>
                        <dt>رقم العقد</dt>
                        <dd>{{ row.code }}</dd>
                      </div>
                      <div>
                        <dt>نوع العقد</dt>
                        <dd>{{ row.typeName }}</dd>
                      </div>
                      <div class="full">
                        <dt>اسم العقد / عنوان الملف</dt>
                        <dd>{{ row.title }}</dd>
                      </div>
                      <div>
                        <dt>العميل / الطرف الثاني</dt>
                        <dd>
                          {{ row.clientName || '—' }}
                          <span class="d-block contract-phone small mt-1">{{ row.clientMobile }}</span>
                        </dd>
                      </div>
                      <div>
                        <dt>جالب العقد (الموظف المسؤول)</dt>
                        <dd>
                          {{ row.employeeName || '—' }}
                          <span class="d-block contract-phone small mt-1">{{ row.employeeMobile }}</span>
                        </dd>
                      </div>
                      <div>
                        <dt>تاريخ البداية</dt>
                        <dd>{{ dateOnly(row.startDate) }}</dd>
                      </div>
                      <div>
                        <dt>تاريخ النهاية</dt>
                        <dd>{{ dateOnly(row.endDate) }}</dd>
                      </div>
                      <div>
                        <dt>عدد العقارات</dt>
                        <dd>{{ number(row.propertiesCount) }}</dd>
                      </div>
                      <div>
                        <dt>النسبة المتفق عليها</dt>
                        <dd>{{ percentage(row.percentage) }}%</dd>
                      </div>
                      <div>
                        <dt>الحالة المحددة</dt>
                        <dd>{{ statusLabel(row.status) }}</dd>
                      </div>
                      <div>
                        <dt>الحالة الحالية حسب المدة</dt>
                        <dd>{{ statusLabel(row.effectiveStatus) }}</dd>
                      </div>
                      <div v-if="row.typeKey === 'property_management'">
                        <dt>وجود ملحق للعقد</dt>
                        <dd>{{ row.hasAnnex ? 'يوجد ملحق' : 'بدون ملحق' }}</dd>
                      </div>
                      <div v-if="row.parentContractId" class="full">
                        <dt>عقد إدارة الأملاك الأساسي</dt>
                        <dd>
                          <router-link :to="`/contracts/view/${row.parentContractId}`" class="contract-title-link">
                            {{ row.parentContractCode }} — {{ row.parentContractTitle }}
                          </router-link>
                        </dd>
                      </div>
                      <div v-else-if="row.typeKey === 'management_annex'" class="full">
                        <dt>عقد إدارة الأملاك الأساسي</dt>
                        <dd>غير متاح</dd>
                      </div>
                      <div class="full">
                        <dt>الملاحظات</dt>
                        <dd class="contract-preserve">{{ row.notes || 'لا توجد ملاحظات' }}</dd>
                      </div>
                      <div>
                        <dt>تاريخ الإضافة</dt>
                        <dd>{{ dateOnly(row.createdAt) }}</dd>
                      </div>
                      <div>
                        <dt>آخر تحديث</dt>
                        <dd>{{ dateOnly(row.updatedAt) }}</dd>
                      </div>
                    </dl>
                    <p class="contract-print-note contract-hint mt-4 mb-0">
                      هذا ملخص للبيانات المسجلة في المنصة. تُراجع نسخة العقد المرفقة للاطلاع على البنود والاتفاقات
                      المعتمدة.
                    </p>
                  </div>
                </section>
                <section
                  v-if="row.attachment"
                  class="contract-card contract-no-print"
                  aria-labelledby="contract-preview-title"
                >
                  <div class="contract-card-header">
                    <h2 id="contract-preview-title">معاينة المرفق</h2>
                    <button
                      v-if="previewable && !previewUrl"
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="previewLoading"
                      @click="loadPreview"
                    >
                      {{ previewLoading ? 'جارٍ تحميل المعاينة...' : 'عرض المرفق' }}
                    </button>
                  </div>
                  <div class="contract-card-body">
                    <div v-if="previewLoading" class="py-4 text-center">
                      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                      <span class="ms-2">جارٍ تحميل المعاينة...</span>
                    </div>
                    <img
                      v-else-if="previewUrl && attachmentKind(row.attachment) === 'image'"
                      :src="previewUrl"
                      :alt="`مرفق العقد ${row.code}`"
                      class="contract-preview-image"
                    />
                    <iframe
                      v-else-if="previewUrl && attachmentKind(row.attachment) === 'pdf'"
                      :src="previewUrl"
                      :title="`معاينة مرفق العقد ${row.code}`"
                      class="contract-preview-pdf"
                    ></iframe>
                    <div v-else class="contract-empty py-4">
                      <i class="bi bi-file-earmark" aria-hidden="true"></i>
                      <p class="mt-3 mb-0">
                        {{
                          previewable
                            ? 'اضغط على «عرض المرفق» لتحميل المعاينة.'
                            : 'هذا ملف Word. نزّل الملف لفتحه في تطبيق يدعم هذا التنسيق.'
                        }}
                      </p>
                    </div>
                    <p v-if="previewUrl && attachmentKind(row.attachment) === 'pdf'" class="contract-hint mt-2 mb-0">
                      إذا لم يعرض المتصفح الملف، استخدم زر تنزيل المرفق.
                    </p>
                    <p v-if="previewError" class="text-danger small mt-2 mb-0" role="alert">{{ previewError }}</p>
                  </div>
                </section>
              </div>
              <aside class="contract-form-aside contract-no-print">
                <section class="contract-card mb-4" aria-labelledby="contract-file-title">
                  <div class="contract-card-header"><h2 id="contract-file-title">نسخة العقد</h2></div>
                  <div class="contract-card-body">
                    <div v-if="row.attachment" class="contract-file-box">
                      <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
                      <p class="contract-file-name fw-bold mt-2 mb-1">{{ row.attachment.name }}</p>
                      <p class="contract-hint">{{ fileSize(row.attachment.size) }}</p>
                      <button
                        type="button"
                        class="btn btn-primary w-100"
                        :disabled="downloading"
                        @click="downloadAttachment"
                      >
                        <i class="bi bi-download ms-1" aria-hidden="true"></i>
                        {{ downloading ? 'جارٍ التنزيل...' : 'تنزيل المرفق' }}
                      </button>
                    </div>
                    <p v-else class="contract-hint mb-0">لم يُرفق ملف بهذا العقد.</p>
                  </div>
                </section>
                <section v-if="row.typeKey === 'property_management' && canCreate" class="contract-card mb-4">
                  <div class="contract-card-header"><h2>ملحق إدارة الأملاك</h2></div>
                  <div class="contract-card-body">
                    <p class="contract-hint">أضف ملحقاً مرتبطاً بهذا العقد، مع تعبئة بيانات العميل والموظف تلقائياً.</p>
                    <router-link
                      :to="{ path: '/contracts/create', query: { type: 'management_annex', parentContractId: row.id } }"
                      class="btn btn-outline-primary w-100"
                    >
                      <i class="bi bi-link-45deg ms-1" aria-hidden="true"></i>
                      إضافة ملحق مرتبط
                    </router-link>
                  </div>
                </section>
                <section class="contract-card">
                  <div class="contract-card-header"><h2>نماذج العقود</h2></div>
                  <div class="contract-card-body">
                    <p class="contract-hint">استعرض النماذج المرفوعة للأنواع المعتمدة في المنصة.</p>
                    <router-link to="/contracts/templates" class="btn btn-outline-primary w-100">
                      فتح مكتبة النماذج
                    </router-link>
                  </div>
                </section>
              </aside>
            </div>
          </template>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, toast } from '@/helpers/tools';
import {
  statusLabel,
  number,
  percentage,
  dateOnly,
  fileSize,
  attachmentKind,
  authenticatedDownload,
  errorMessage,
} from './helpers';
import './contracts.css';

export default {
  name: 'ContractView',
  data() {
    return {
      row: null,
      loading: true,
      hasError: false,
      message: '',
      errorNumber: 0,
      serial: 0,
      downloading: false,
      previewUrl: '',
      previewLoading: false,
      previewError: '',
    };
  },
  computed: {
    canRead() {
      return checkAccess('contracts_read');
    },
    canUpdate() {
      return checkAccess('contracts_update');
    },
    canCreate() {
      return checkAccess('contracts_create');
    },
    previewable() {
      return ['image', 'pdf'].includes(attachmentKind(this.row?.attachment));
    },
  },
  watch: {
    '$route.params.id'() {
      if (/^\/contracts\/view\//.test(this.$route.path)) this.load();
    },
  },
  methods: {
    statusLabel,
    number,
    percentage,
    dateOnly,
    fileSize,
    attachmentKind,
    clearPreview() {
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = '';
      this.previewLoading = false;
      this.previewError = '';
    },
    async load() {
      const serial = ++this.serial;
      this.clearPreview();
      this.row = null;
      this.hasError = false;
      if (!this.canRead) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const { data } = await this.$http.get(`/api/admin/contracts/retrieve/${this.$route.params.id}`);
        if (serial === this.serial) this.row = data.row;
      } catch (error) {
        if (serial === this.serial) {
          [this.message, this.errorNumber] = this.$globalError(error, true);
          this.hasError = true;
        }
      } finally {
        if (serial === this.serial) this.loading = false;
      }
    },
    async loadPreview() {
      if (!this.previewable || this.previewLoading || this.previewUrl) return;
      const serial = this.serial;
      this.previewLoading = true;
      this.previewError = '';
      try {
        const { data } = await this.$http.get(`/api/admin/contracts/attachment/${this.row.id}`, {
          responseType: 'blob',
        });
        if (serial !== this.serial) return;
        const kind = attachmentKind(this.row.attachment);
        this.previewUrl = URL.createObjectURL(
          new Blob([data], { type: kind === 'pdf' ? 'application/pdf' : this.row.attachment.mimeType || data.type })
        );
      } catch (error) {
        if (serial === this.serial) this.previewError = await errorMessage(this, error);
      } finally {
        if (serial === this.serial) this.previewLoading = false;
      }
    },
    async downloadAttachment() {
      if (!this.row?.attachment || this.downloading) return;
      this.downloading = true;
      try {
        await authenticatedDownload(
          this.$http,
          `/api/admin/contracts/attachment/${this.row.id}`,
          this.row.attachment.name
        );
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.downloading = false;
      }
    },
    printSummary() {
      window.print();
    },
  },
  created() {
    this.load();
  },
  beforeUnmount() {
    this.serial += 1;
    this.clearPreview();
  },
};
</script>
